import { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import useSEO from '@/hooks/useSEO';
import { motion, useInView } from 'framer-motion';
import { 
  ArrowRight, 
  Download,
  ExternalLink,
  ChevronRight,
  FileText,
  Phone,
  Shield
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Section component with scroll animation
const Section = ({ children, className = '' }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  
  return (
    <motion.section
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
      transition={{ duration: 0.7 }}
      className={className}
    >
      {children}
    </motion.section>
  );
};

const ReglementationPage = () => {
  useSEO({
    title: 'Réglementation location Airbnb | Guide',
    description: 'Comprenez la réglementation des locations Airbnb avec La conciergerie Confort Hôte : obligations légales, déclaration, fiscalité et règles pour la location saisonnière.',
    keywords: 'réglementation Airbnb, conciergerie, confort hôte, Rennes, conciergerie Vitré, conciergerie Dol-de-Bretagne, conciergerie Pleine-Fougères, Confort Hôte',
  });

  const [regulations, setRegulations] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchRegulations = async () => {
      try {
        const response = await axios.get(`${API}/regulations`);
        setRegulations(response.data);
      } catch (error) {
        console.error('Error fetching regulations:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchRegulations();
  }, []);

  const cerfaUrl = "https://www.service-public.fr/particuliers/vosdroits/R14004";

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20" data-testid="reglementation-page">
      {/* Hero Section */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-3xl">
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-xs uppercase tracking-[0.3em] text-[#C5A059] mb-6"
            >
              Guide pratique
            </motion.p>
            
            <motion.h1 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="text-4xl sm:text-5xl lg:text-6xl font-playfair font-medium tracking-tight leading-[1.1] text-[#F5F5F5] mb-8"
            >
              Réglementation<br/>
              <span className="text-gold-gradient">Airbnb</span>
            </motion.h1>
            
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="text-lg md:text-xl text-[#A3A3A3] font-light leading-relaxed mb-8"
            >
              Comprendre les obligations légales pour louer votre bien en courte durée. 
              Chaque commune a ses spécificités : découvrez les règles applicables dans votre ville.
            </motion.p>

            <motion.a
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              href={cerfaUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 bg-[#C5A059] text-black px-8 py-4 text-sm uppercase tracking-[0.15em] font-semibold hover:bg-[#D4AF37] transition-colors duration-300"
              data-testid="download-cerfa"
            >
              <Download className="w-4 h-4" />
              <span>Télécharger le CERFA 14004</span>
            </motion.a>
          </div>
        </div>
      </section>

      {/* General Info Section */}
      <Section className="py-24 md:py-32 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            <div>
              <p className="text-xs uppercase tracking-[0.3em] text-[#C5A059] mb-6">
                Cadre général
              </p>
              <h2 className="text-3xl sm:text-4xl font-playfair font-normal tracking-tight leading-tight text-[#F5F5F5] mb-8">
                Ce que dit la loi
              </h2>
              <div className="space-y-6 text-[#A3A3A3] font-light leading-relaxed">
                <p>
                  La location de courte durée (type Airbnb) est encadrée par la loi ALUR et 
                  la loi ELAN. Les règles varient selon qu'il s'agit de votre résidence 
                  principale ou secondaire.
                </p>
                <p>
                  <strong className="text-[#F5F5F5]">Résidence principale :</strong> Vous pouvez louer 
                  votre logement jusqu'à 120 jours par an sans autorisation particulière, 
                  sous réserve de déclaration en mairie dans certaines communes.
                </p>
                <p>
                  <strong className="text-[#F5F5F5]">Résidence secondaire :</strong> Une autorisation 
                  de changement d'usage peut être requise, notamment dans les zones tendues. 
                  Les règles sont plus strictes.
                </p>
              </div>
            </div>

            <div className="glass-card p-8 md:p-10">
              <p className="text-xs uppercase tracking-[0.3em] text-[#C5A059] mb-6">
                À retenir
              </p>
              <div className="space-y-6">
                {[
                  {
                    title: "Déclaration en mairie",
                    desc: "Obligatoire dans la plupart des communes pour obtenir un numéro d'enregistrement."
                  },
                  {
                    title: "Limite 120 jours",
                    desc: "Pour les résidences principales, la location est limitée à 120 jours par an."
                  },
                  {
                    title: "Taxe de séjour",
                    desc: "À collecter auprès des voyageurs et reverser à la collectivité."
                  },
                  {
                    title: "Numéro d'enregistrement",
                    desc: "Doit figurer sur toutes vos annonces de location."
                  }
                ].map((item, index) => (
                  <div key={index} className="pb-6 border-b border-[#262626] last:border-0 last:pb-0">
                    <h3 className="text-lg font-playfair text-[#F5F5F5] mb-2">{item.title}</h3>
                    <p className="text-[#A3A3A3] font-light">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </Section>

      {/* Insurance Section */}
      <Section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-xs uppercase tracking-[0.3em] text-[#C5A059] mb-6">
                Assurance obligatoire
              </p>
              <h2 className="text-3xl sm:text-4xl font-playfair font-normal tracking-tight leading-tight text-[#F5F5F5] mb-8">
                Protégez votre activité<br/>de location
              </h2>
              <div className="space-y-6 text-[#A3A3A3] font-light leading-relaxed">
                <p>
                  Pour exercer une activité de location courte durée, une <strong className="text-[#F5F5F5]">assurance spécifique</strong> est 
                  indispensable. Votre assurance habitation classique ne couvre généralement pas 
                  les risques liés à cette activité.
                </p>
                <p>
                  Cette assurance doit couvrir : la responsabilité civile professionnelle, les 
                  dégâts causés par les voyageurs, le vol, et les pertes d'exploitation éventuelles.
                </p>
                <p>
                  <strong className="text-[#F5F5F5]">Confort Hôte exige cette assurance</strong> pour prendre en charge 
                  le pilotage de votre bien. C'est une garantie de sérieux pour vous comme pour vos voyageurs.
                </p>
              </div>
            </div>
            
            <div className="bg-[#0A0A0A] border border-[#262626] p-8 md:p-10">
              <div className="flex items-center gap-3 mb-6">
                <Shield className="w-8 h-8 text-[#C5A059]" />
                <h3 className="text-xl font-playfair text-[#F5F5F5]">Notre partenaire assurance</h3>
              </div>
              <p className="text-[#A3A3A3] font-light leading-relaxed mb-6">
                Nous travaillons avec <strong className="text-[#F5F5F5]">Hostprotect</strong>, spécialiste de l'assurance 
                pour la location courte durée. Couverture complète, tarifs adaptés, souscription simplifiée.
              </p>
              <a
                href="https://hostprotect.io"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-[#C5A059] hover:text-[#D4AF37] transition-colors duration-300"
              >
                <span className="text-sm uppercase tracking-wider">Découvrir Hostprotect</span>
                <ExternalLink className="w-4 h-4" />
              </a>
            </div>
          </div>
        </div>
      </Section>

      {/* Cities Regulations Section */}
      <Section className="py-24 md:py-32 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <p className="text-xs uppercase tracking-[0.3em] text-[#C5A059] mb-6">
              Par ville
            </p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-playfair font-normal tracking-tight text-[#F5F5F5]">
              Réglementation locale
            </h2>
          </div>

          <div className="space-y-6">
            {regulations && Object.entries(regulations).map(([slug, data]) => (
              <div 
                key={slug}
                className="bg-[#0A0A0A] border border-[#262626] p-8 md:p-10 hover:border-[#C5A059]/20 transition-colors duration-500"
                data-testid={`regulation-${slug}`}
              >
                <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-8">
                  <div className="flex-1">
                    <div className="flex items-center gap-4 mb-4">
                      <h3 className="text-2xl font-playfair text-[#F5F5F5]">{data.name}</h3>
                      <a 
                        href={data.mairie_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1 text-sm text-[#C5A059] hover:text-[#D4AF37] transition-colors"
                      >
                        <span>Site mairie</span>
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    </div>
                    <ul className="space-y-3">
                      {data.rules.map((rule, index) => (
                        <li key={index} className="flex items-start gap-3">
                          <div className="w-1.5 h-1.5 bg-[#C5A059] rounded-full mt-2" />
                          <span className="text-[#A3A3A3] font-light">{rule}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <Link
                    to={`/conciergerie/${slug}`}
                    className="inline-flex items-center gap-2 text-[#C5A059] hover:text-[#D4AF37] transition-colors duration-300 whitespace-nowrap"
                  >
                    <span className="text-sm uppercase tracking-wider">Conciergerie à {data.name}</span>
                    <ChevronRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </Section>

      {/* CERFA Download Section */}
      <Section className="py-24 md:py-32 bg-[#0A0A0A]">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <div className="glass-card p-8 md:p-12 text-center">
            <FileText className="w-16 h-16 text-[#C5A059] mx-auto mb-6" />
            <h2 className="text-2xl md:text-3xl font-playfair text-[#F5F5F5] mb-4">
              Formulaire CERFA 14004
            </h2>
            <p className="text-[#A3A3A3] font-light leading-relaxed mb-8 max-w-xl mx-auto">
              Ce formulaire est nécessaire pour déclarer votre meublé de tourisme en mairie 
              et obtenir votre numéro d'enregistrement obligatoire.
            </p>
            <a
              href={cerfaUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 bg-[#C5A059] text-black px-8 py-4 text-sm uppercase tracking-[0.15em] font-semibold hover:bg-[#D4AF37] transition-colors duration-300"
            >
              <Download className="w-4 h-4" />
              <span>Accéder au formulaire</span>
            </a>
          </div>
        </div>
      </Section>

      {/* CTA Section */}
      <Section className="py-24 md:py-32">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-playfair font-normal tracking-tight leading-tight text-[#F5F5F5] mb-6">
            Besoin d'accompagnement ?
          </h2>
          <p className="text-lg text-[#A3A3A3] font-light leading-relaxed mb-10 max-w-2xl mx-auto">
            Confort Hôte prend en charge toutes les démarches administratives pour vous. 
            Déclaration, taxe de séjour, conformité : nous gérons tout.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="inline-flex items-center justify-center gap-2 bg-[#C5A059] text-black px-8 py-4 text-sm uppercase tracking-[0.15em] font-semibold hover:bg-[#D4AF37] transition-colors duration-300"
              data-testid="cta-audit-reglementation"
            >
              <span>Demander un audit gratuit</span>
              <ArrowRight className="w-4 h-4" />
            </Link>
            <a
              href="tel:0689224402"
              className="inline-flex items-center justify-center gap-2 border border-[#C5A059]/30 text-[#C5A059] px-8 py-4 text-sm uppercase tracking-[0.15em] hover:border-[#C5A059] hover:bg-[#C5A059]/5 transition-colors duration-300"
            >
              <Phone className="w-4 h-4" />
              <span>06 89 22 44 02</span>
            </a>
          </div>
        </div>
      </Section>
    </div>
  );
};

export default ReglementationPage;
